import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { headingsSchema } from '../Header';
import TitledSection from './TitledSection';

const {
    schema: generatedSchema,
    uiSchema: generatedUiSchema,
    group: generatedGroup,
    dependencies: generatedDependencies,
} = customSchemas.GenerateAnalyticsSchema(['Toggle Section']);

const schema: JSONSchema7 = {
    title: 'Titled Section',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        id: {
            type: 'string',
            title: 'Section Id',
        },
        title: {
            type: 'string',
            title: 'Title',
            description: 'Section title',
        },
        titles: headingsSchema,
        isCollapsible: {
            type: 'boolean',
            enum: [true, false],
            description: 'Make section collapsible or not',
            default: false,
            title: 'Collapsible',
        },
        toggleSection: {
            type: 'boolean',
            enum: [true, false],
            description: 'Make section toggled',
            default: false,
            title: 'Toggle Section',
        },
        isTransformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        showToolTip: {
            type: 'boolean',
            title: 'Show tooltip',
            default: false,
        },
        tooltipOptions: {
            title: 'Configure tooltip options',
            type: 'object',
            properties: {
                text: {
                    type: 'string',
                    title: 'Enter text',
                },
                iconName: {
                    type: 'string',
                    title: 'Enter tootltip icon',
                    default: 'fas fa-info-circle',
                },
                backgroundColor: {
                    type: 'string',
                    title: 'Enter background color',
                },
            },
        },
        ...generatedSchema,
        disableTabStaticData: {
            type: 'boolean',
            title: 'Disable tabbing through static data',
            description: '',
        },
    },
    dependencies: {
        isCollapsible: {
            oneOf: [
                {
                    properties: {
                        isCollapsible: { enum: [true] },
                        showIcon: {
                            type: 'boolean',
                            enum: [true, false],
                            description: 'Show icon for collapsible',
                            default: false,
                            title: 'Show Collapsible Icon',
                        },
                        defaultCollpsible: {
                            type: 'string',
                            enum: ['Show', 'Hidden'],
                            description: 'Make section collapsed when first render or not',
                            default: 'Show',
                            title: 'Default Collapsible Behaviour',
                        },
                    },
                },
                {
                    properties: {
                        isCollapsible: { enum: [false] },
                    },
                },
            ],
        },
        showIcon: {
            oneOf: [
                {
                    properties: {
                        showIcon: { enum: [true] },
                        iconShowContent: {
                            type: 'string',
                            title: 'Show Content Icon Name',
                            default: 'fas fa-plus-circle',
                        },
                        iconHideContent: {
                            type: 'string',
                            title: 'Hide Content Icon Name',
                            default: 'fas fa-minus-circle',
                        },
                        iconPosition: {
                            type: 'string',
                            title: 'Collapsible Icon Position',
                            enum: ['Right', 'Left'],
                            default: 'Left',
                        },
                    },
                    required: ['iconShowContent', 'iconHideContent'],
                },
                {
                    properties: {
                        showIcon: { enum: [false] },
                    },
                },
            ],
        },
        toggleSection: {
            oneOf: [
                {
                    properties: {
                        toggleSection: { enum: [true] },
                        toggleLabels: {
                            type: 'string',
                            description: 'e.g Chart,Table',
                            title: 'Toggle Labels with comma separeted value',
                            default: 'Chart,Table',
                        },
                        analyticsLabels: {
                            type: 'string',
                            title: 'Toogle Section Label for analytics',
                            default: 'Chart,Table',
                        },
                        defaultActiveSection: {
                            type: 'string',
                            title: 'Default Active Section',
                            default: 'on',
                        },
                    },
                },
                {
                    properties: {
                        toggleSection: { enum: [false] },
                    },
                },
            ],
        },
        isTransformData: {
            oneOf: [
                {
                    properties: {
                        isTransformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default:
                                // eslint-disable-next-line max-len
                                'return (data) => { return {titles, children, titledSection} }',
                        },
                    },
                },
            ],
        },
        ...generatedDependencies,
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'id',
            'title',
            'titles',
            'isCollapsible',
            'showIcon',
            'iconShowContent',
            'iconHideContent',
            'iconPosition',
            'defaultCollpsible',
            'toggleSection',
            'toggleLabels',
            'analyticsLabels',
            'defaultActiveSection',
            'showToolTip',
            'tooltipOptions',
        ],
        ...generatedGroup,
        'Transform Data': ['isTransformData', 'functionToTransform'],
        Accessibility: ['disableTabStaticData'],
    },
];

const TitledSectionWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        ...generatedUiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        titles: {
            items: {
                headerText: {
                    'ui:widget': 'ResourcePicker',
                },
            },
        },
        functionToTransform: { 'ui:widget': 'JSEditor' },
        disableTabStaticData: { 'ui:widget': 'CustomCheckbox' },
    },
    templateView: TitledSection,
    id: 'TitledSection',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        title: 'Title',
        defaultCollpsible: 'Show',
        defaultActiveSection: 'on',
    }),
};

export default TitledSectionWidget;
